import React, { useState } from "react";
import { images } from "../../Assets";
import { Button } from "../../Components/ui/button";
import { Modal, ModalContent } from "../../Components/ui/modal";
import {
  useGetIndividualOrderQuery,
  useCancelOrderMutation,
  useReassignOrderMutation,
} from "../Orders/OrderApi";
import { format, parseISO } from "date-fns";
import { SkeletonTable } from "../../Components/ui/skeleton";
import { useGetRiderLocationQuery } from "./DashboardApi";
import { writeClipboardText } from "../../Lib/utils";
import MapComponentBeta from "../../Components/MapComponentBeta";
import RiderStatusTimeline from "./RiderStatusTimeline";
import { Combobox } from "../../Components/ui/combobox";
import { useSelector } from "react-redux";
import Spinner from "../../Assets/spinner";
import toast from "react-hot-toast";

const STATUS_VARIANT_MAP = {
  pending: "pending",
  accepted: "accepted",
  picked_up: "picked_up",
  in_progress: "in_progress",
  delivered: "delivered",
  cancelled: "cancelled",
  declined: "declined",
  expired: "expired",
};

const PAYMENT_VARIANT_MAP = {
  paid: "successful",
  unpaid: "pending",
};

// Assumption — verify against real backend dispatchStatus enum during testing.
const DISPATCH_STATUS_VARIANT_MAP = {
  assigned: "accepted",
  accepted: "accepted",
  unassigned: "pending",
  declined: "declined",
  expired: "expired",
};

const CANCELLABLE_STATUSES = ["pending", "accepted"];


const Field = ({ label, children }) => (
  <div>
    <p className='text-xs text-gray-600 font-semibold'>{label}</p>
    <p className='text-primaryBlack font-normal text-sm break-words'>
      {children}
    </p>
  </div>
);

const TrackOrder = ({ id }) => {
  const [cancelOpen, setCancelOpen] = useState(false);
  const { data, isLoading } = useGetIndividualOrderQuery(id);
  const [reAssign, { isLoading: reassignLoader }] = useReassignOrderMutation();
  const [cancelOrder, { isLoading: cancelling }] = useCancelOrderMutation();
  const { availableRiders } = useSelector((state) => state?.location);

  const order = data?.data;

  const { data: riderLocation } = useGetRiderLocationQuery(order?.riderId, {
    skip: isLoading || !order?.riderId,
    pollingInterval: 5000,
    skipPollingIfUnfocused: true,
  });
  const [rider, setRider] = useState("");

  const handleReassignOrder = async () => {
    try {
      const body = {
        orderId: id,
        riderId: rider,
      };
      const { data, error } = await reAssign(body);
      if (data?.statusCode < 300) {
        toast.success("Rider Reassigned successfuly", { id: "reassign" });
      } else if (!error) {
        // A server error is already shown with its message by the base query
        toast.error("Please try again", { id: "reassignErr" });
      }
    } catch (error) {
      toast.error("Please try again", { id: "reassignErr" });
    }
  };

  const handleCancel = async () => {
    try {
      const res = await cancelOrder(id).unwrap();
      if (res?.statusCode < 300) {
        toast.success("Order cancelled", { id: "orderCancel" });
        setCancelOpen(false);
      }
    } catch {
      toast.error("Action failed, please try again", { id: "orderErr" });
    }
  };

  if (isLoading)
    return (
      <div className='w-full'>
        <SkeletonTable />
      </div>
    );

  const statusVariant = STATUS_VARIANT_MAP[order?.currentStatus] || "pending";
  const paymentVariant = PAYMENT_VARIANT_MAP[order?.paymentStatus] || "pending";
  const dispatchVariant = DISPATCH_STATUS_VARIANT_MAP[order?.dispatchStatus] || "pending";

  // Same rules as the server: only a pending order with no rider on it. Auto-dispatch owns a
  // WhatsApp order until its search fails, so only a failed one can be reassigned
  const canReassign =
    !!order &&
    order.currentStatus === "pending" &&
    (order.channel === "whatsapp" ? order.dispatchStatus === "failed" : !order.riderId);

  return (
    <div className='w-full'>
      <div className='grid md:grid-cols-4 gap-5'>
        <Field label='Order Date and Time'>
          {order?.createdAt
            ? format(parseISO(order.createdAt), "p, MMM d, yyyy")
            : "N/A"}
        </Field>
        {order?.item && <Field label='Item'>{order.item}</Field>}
        <Field label='Order Status'>
          <Button asChild variant={statusVariant} size='sm'>
            <p className='capitalize'>
              {order?.currentStatus
                ? order.currentStatus.replace(/_/g, " ")
                : "N/A"}
            </p>
          </Button>
        </Field>
        <Field label='Payment Status'>
          <Button asChild variant={paymentVariant} size='sm'>
            <p className='capitalize'>{order?.paymentStatus || "N/A"}</p>
          </Button>
        </Field>
        <Field label='Dispatch Status'>
          <Button asChild variant={dispatchVariant} size='sm'>
            <p className='capitalize'>
              {order?.dispatchStatus
                ? order.dispatchStatus.replace(/_/g, " ")
                : "Unassigned"}
            </p>
          </Button>
        </Field>
      </div>

      <div className='border-t border-gray-100 mt-4 pt-4'>
        <p className='text-xs text-gray-600 font-semibold mb-1'>
          Rider Details
        </p>
        <div className='grid md:grid-cols-3 gap-5'>
          <Field label='Name'>
            {order?.rider
              ? `${order.rider.firstName || ""} ${order.rider.lastName || ""}`.trim() ||
                "N/A"
              : "No rider assigned yet"}
          </Field>
          <Field label='Phone Number'>
            {order?.rider?.phoneNumber || "N/A"}
          </Field>
        </div>
      </div>

      <div
        className='w-full my-5 grid grid-cols-1 rounded-md overflow-hidden'
        style={{ height: 320 }}
      >
        <MapComponentBeta enRoute={riderLocation} />
      </div>

      <div className='border-t border-gray-100 pt-4'>
        <p className='text-xs text-gray-600 font-semibold mb-2'>
          Delivery Progress
        </p>
        <RiderStatusTimeline
          statuses={order?.status ?? []}
          currentStatus={order?.currentStatus}
        />
      </div>

      <div className='rounded-md border border-gray-100 p-3 mt-4'>
        <div className='flex gap-2 pb-3 border-b border-gray-100'>
          <img src={images.pickup} alt='pickup' />
          <Field label='Pick up'>
            {order?.pickupLocation?.address || "N/A"}
          </Field>
        </div>
        <div className='flex gap-2 pt-3'>
          <img src={images.dropOff} alt='dropOff' />
          <Field label='Drop off'>{order?.deliveryAddress || "N/A"}</Field>
        </div>
      </div>

      <div className='border-t border-gray-100 mt-4 pt-4'>
        <p className='text-xs text-gray-600 font-semibold mb-1'>
          Customer Details
        </p>
        <div className='grid md:grid-cols-3 gap-5'>
          <Field label='Name'>
            <span className='capitalize'>{order?.customerName || "N/A"}</span>
          </Field>
          <Field label='Phone Number'>
            {order?.customerPhoneNumber || "N/A"}
          </Field>
          <Field label='Delivery Code'>
            <Button
              variant='ghost'
              className='lowercase flex items-center text-center gap-1 text-primaryBlack font-bold px-0'
              onClick={() =>
                writeClipboardText(
                  "OTP copied successfuly",
                  order?.deliveryCode,
                )
              }
            >
              {order?.deliveryCode} <img src={images.copy} alt='copy' />
            </Button>
          </Field>
        </div>
      </div>

      {order?.extraDeliveryNote && (
        <div className='border-t border-gray-100 mt-4 pt-4'>
          <p className='text-xs text-gray-600 font-semibold mb-1'>
            Delivery Note
          </p>
          <p className='text-primaryBlack font-normal text-sm break-words'>
            {order.extraDeliveryNote}
          </p>
        </div>
      )}

      {canReassign && (
        <div className='border-t border-gray-100 mt-4 pt-4'>
          <p className='text-xs text-gray-600 font-semibold mb-2'>
            Re-assign Rider
          </p>
          <div className='flex gap-4'>
            <div className='w-1/2'>
              <Combobox
                frameworks={availableRiders}
                setValue={(val) => {
                  setRider(val?.value);
                }}
                value={rider}
                placeholder='New Rider'
              />
            </div>
            <Button variant='outline' onClick={handleReassignOrder}>
              {reassignLoader ? <Spinner /> : "Re-Assign"}
            </Button>
          </div>
        </div>
      )}

      {CANCELLABLE_STATUSES.includes(order?.currentStatus) && (
        <div className='border-t border-gray-100 mt-4 pt-4 flex justify-end'>
          <Button variant='failed' onClick={() => setCancelOpen(true)}>
            Cancel Order
          </Button>
        </div>
      )}

      <Modal open={cancelOpen} onOpenChange={setCancelOpen}>
        <ModalContent name='Cancel Order?'>
          <p className='text-sm text-primaryBlack my-4'>
            Are you sure you want to cancel this order? This action cannot be
            undone.
          </p>
          <div className='flex justify-between items-center bg-white w-[100%]'>
            <Button
              variant='successful'
              className='w-[30%]'
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling ? <Spinner /> : "Yes"}
            </Button>
            <Button
              variant='failed'
              className='w-[30%]'
              onClick={() => setCancelOpen(false)}
            >
              No
            </Button>
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default TrackOrder;
